import Vue from 'vue';
import _ from 'lodash';
import { account } from '@/services';
import AppConfig from '@/config';
import {
    getters as $G,
    actions as $A,
    mutations as $M
} from '../types';


const state = {
    ready: false,
    theme: undefined,
    userPreferences: undefined,
    groupRoles: undefined,
    authError: undefined,
    userDashboardPermissions: undefined,
};

export const getters = {
    [$G.ALLOW_PERSISTENT_SESSIONS]() {
        return AppConfig.auth.allowPersistentSessions === true;
    },
    [$G.AUTH_ERROR]() {
        return state.authError;
    },
    [$G.READY](state) {
        return state.ready === true;
    },
    [$G.THEME](state, getters) {
        return state.theme;
    },
    [$G.USER_GROUP_IS_ADMIN]: (state, getters) => (id) => {
        const role = getters[$G.USER_GROUP_ROLE_BY_ID](id);
        if (typeof role === 'undefined') return false;

        return role === 2; // Group admin
    },
    [$G.USER_GROUP_ROLE_BY_ID]: (state, getters) => (id) => {
        const groupRoles = state.groupRoles;
        if (!groupRoles) return undefined;

        const groupRole = _.find(groupRoles, (gr) => gr.locationGroupID === id);
        if (!groupRole) return undefined;

        return groupRole.role;
    },
    [$G.USER_GROUPS_IS_ADMIN](state, getters) {
        if (!state.groupRoles || !state.groupRoles.length) return false;

        return _.some(state.groupRoles, (r) => getters[$G.USER_GROUP_IS_ADMIN](r.locationGroupID));
    },
    // method must exist to provide for the widget template functionality in the dashboard_pdk, even though this feature is not used in Ops.
    [$G.USER_PERMISSION](state, getters) {
        return [];
    },
    [$G.USER_PREFERENCES](state, getters) {
        return state.userPreferences;
    },
    [$G.USER_DASHBOARD_PERMISSIONS](state) {
        return state.userDashboardPermissions;
    },
    [$G.USER_IS_DASHBOARD_ADMIN]: (state, getters) => (dashboardId) => {
        const permissions = getters[$G.USER_DASHBOARD_PERMISSIONS];
        const dashboardPermission = _.find(permissions, (p) => p.dashboardID === dashboardId);
        
        if (typeof dashboardPermission === 'undefined') return false;

        return dashboardPermission.dashboardPermission == 4; // Dashboard admin permission
    },
    [$G.USER_ID]() {
        return Vue.$auth.tokenParsed.GlobalUserId;
    }
};

export const actions = {
    [$A.LAST_LOGIN_UPDATE](context) {
        return new Promise((resolve, reject) => {
            account.updateLastLogin()
                .then(() => {
                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
        });
    },
    [$A.LOGOUT](context) {
        return new Promise((resolve) => {
            context.commit($M.USER_PREFERENCES_CLEAR);
            context.commit($M.USER_GROUP_ROLES_CLEAR);

            var basePath = window.location.origin.toString();
            Vue.$auth.logout({ redirectUri: basePath });

            resolve();
        });
    },
    [$A.THEME_LOAD](context) {
        return new Promise((resolve, reject) => {
            if (!context.state.theme) {
                account.getThemePreference()
                .then((preferences) => {
                    const theme = preferences.theme || 'light';
                    context.commit($M.THEME_SET, {
                        theme
                    });
                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
            }
            else
                resolve();
        });
    },
    [$A.THEME_SET](context, payload) {
        const theme = payload.theme;
        return new Promise((resolve, reject) => {
             account.updateThemePreference(payload)
                .then(() => {
                    context.commit($M.THEME_SET, {
                        theme
                    });
                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
            });
    },
    [$A.USER_GROUP_ROLES_LOAD](context) {
        return new Promise((resolve, reject) => {
            account.getUserRoles()
                .then((roles) => {
                    const groupRoles = roles.groupRoles;
                    
                    context.commit($M.USER_GROUP_ROLES_SET, {
                        groupRoles
                    });

                    resolve();
                })
                .catch((e) => {
                    context.commit($M.USER_GROUP_ROLES_SET, {
                        groupRoles: []
                    });

                    reject(e);
                });
        });
    },
    [$A.USER_PREFERENCES_LOAD](context) {
        return new Promise((resolve, reject) => {
            account.getUserPreferences()
                .then((preferences) => {
                    const userPreferences = _.cloneDeep(preferences);

                    context.commit($M.USER_PREFERENCES_SET, {
                        userPreferences
                    });

                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
        });
    },
    [$A.USER_PREFERENCES_UPDATE](context, payload) {
        return new Promise((resolve, reject) => {
            return account.updateUserPreferences(payload)
                .then((preferences) => {
                    const userPreferences = _.cloneDeep(preferences);

                    context.commit($M.USER_PREFERENCES_SET, {
                        userPreferences
                    });

                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
        });
    },
    [$A.USER_DASHBOARD_PERMISSIONS_LOAD](context) {
        return new Promise((resolve, reject) => {
            account.getUserDashboardPermissions()
                .then((permissions) => {
                    const userDashboardPermissions = _.cloneDeep(permissions);

                    context.commit($M.USER_DASHBOARD_PERMISSIONS_SET, userDashboardPermissions);

                    resolve();
                })
                .catch((e) => {
                    reject(e);
                });
        });
    },
};

export const mutations = {
    [$M.AUTH_ERROR_SET](state, payload) {
        state.authError = payload.authError;
    },
    [$M.READY_SET](state, payload) {
        state.ready = payload.ready;
    },
    [$M.THEME_SET](state, payload) {
        state.theme = payload.theme;
    },
    [$M.USER_PREFERENCES_CLEAR](state) {
        state.userPreferences = undefined;
    },
    [$M.USER_PREFERENCES_SET](state, payload) {
        state.userPreferences = payload.userPreferences;
    },
    [$M.USER_GROUP_ROLES_CLEAR](state) {
        state.groupRoles = undefined;
    },
    [$M.USER_GROUP_ROLES_SET](state, payload) {
        state.groupRoles = payload.groupRoles;
    },
    [$M.USER_DASHBOARD_PERMISSIONS_SET](state, payload) {
        state.userDashboardPermissions = payload;
    },
};

export default {
    state,
    getters,
    actions,
    mutations
};
